import React, { useState } from 'react';
import { ArrowLeft, Search, MapPin, Briefcase, DollarSign, Clock, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';

const JobBoard = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Engineering', 'Design', 'Marketing', 'Sales', 'Product'];

  const jobs = [
    {
      id: 1,
      title: 'Senior Frontend Developer',
      company: 'TechCorp',
      location: 'San Francisco, CA',
      type: 'Full-time',
      salary: '$120k - $150k',
      category: 'Engineering',
      posted: '2 days ago',
      description: 'We are looking for an experienced frontend developer to join our team...',
    },
    {
      id: 2,
      title: 'UI/UX Designer',
      company: 'DesignStudio',
      location: 'Remote',
      type: 'Full-time',
      salary: '$90k - $120k',
      category: 'Design',
      posted: '1 day ago',
      description: 'Join our creative team to design beautiful and intuitive user experiences...',
    },
    {
      id: 3,
      title: 'Marketing Manager',
      company: 'GrowthCo',
      location: 'New York, NY',
      type: 'Full-time',
      salary: '$100k - $130k',
      category: 'Marketing',
      posted: '3 days ago',
      description: 'Lead our marketing efforts and drive brand awareness...',
    },
    {
      id: 4,
      title: 'Sales Representative',
      company: 'SalesPro',
      location: 'Chicago, IL',
      type: 'Full-time',
      salary: '$70k - $90k',
      category: 'Sales',
      posted: '5 days ago',
      description: 'Build relationships with clients and drive revenue growth...',
    },
    {
      id: 5,
      title: 'Product Manager',
      company: 'ProductLab',
      location: 'Seattle, WA',
      type: 'Full-time',
      salary: '$130k - $160k',
      category: 'Product',
      posted: '1 week ago',
      description: 'Shape the future of our products and work with cross-functional teams...',
    },
    {
      id: 6,
      title: 'React Developer',
      company: 'DevTeam',
      location: 'Remote',
      type: 'Contract',
      salary: '$80 - $100/hr',
      category: 'Engineering',
      posted: '2 days ago',
      description: 'Work on exciting projects using React and modern web technologies...',
    },
  ];

  const filteredJobs =
    selectedCategory === 'All'
      ? jobs.filter(
          (job) =>
            job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            job.company.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : jobs.filter(
          (job) =>
            job.category === selectedCategory &&
            (job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
              job.company.toLowerCase().includes(searchTerm.toLowerCase()))
        );

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/portfolio"
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 mb-8"
        >
          <ArrowLeft size={20} />
          Back to Portfolio
        </Link>

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Job Board</h1>
          <p className="text-gray-600">Find your next career opportunity</p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search jobs, companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full"
              />
            </div>
            <button className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
              <Filter size={20} />
              More Filters
            </button>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Job Listings */}
        <div className="space-y-4">
          {filteredJobs.map((job) => (
            <div
              key={job.id}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Briefcase className="text-primary-600" size={24} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{job.title}</h3>
                      <p className="text-gray-600 mb-2">{job.company}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <MapPin size={16} />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={16} />
                          {job.posted}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign size={16} />
                          {job.salary}
                        </span>
                      </div>
                      <p className="text-gray-600 mt-3">{job.description}</p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium w-fit">
                    {job.category}
                  </span>
                  <button className="px-6 py-2 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors whitespace-nowrap">
                    Apply Now
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default JobBoard;

